import { useEffect, useState } from "react";
import { rgbToHex } from "@/lib/editorRuntime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  X, Trash2, Bold, AlignLeft, AlignCenter, AlignRight, Type, Image as ImageIcon,
  Palette, Square, Link2, Upload, Loader2,
} from "lucide-react";

function ColorField({ label, value, onChange, testid }) {
  const hex = rgbToHex(value);
  return (
    <div className="flex items-center justify-between">
      <Label className="text-xs text-muted-foreground flex items-center gap-1.5">{label}</Label>
      <label className="relative w-8 h-8 rounded-md border border-white/15 overflow-hidden cursor-pointer" style={{ background: hex }}>
        <input
          type="color"
          value={hex}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid={testid}
        />
      </label>
    </div>
  );
}

export default function Inspector({ info, onCmd, onClose, onDelete, onUpload }) {
  const [src, setSrc] = useState("");
  const [href, setHref] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setSrc(info?.src || "");
    setHref(info?.href || "");
  }, [info]);

  if (!info) return null;
  const isBold = parseInt(info.fontWeight, 10) >= 600;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUpload(file);
      if (url) {
        setSrc(url);
        onCmd("src", { value: url });
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className="absolute top-0 right-0 h-full w-72 max-w-[85%] bg-[#141414] border-l border-white/10 z-20 flex flex-col"
      data-testid="inspector-panel"
    >
      <div className="flex items-center justify-between px-4 h-12 border-b border-white/10 shrink-0">
        <span className="text-xs font-mono uppercase tracking-wider text-primary">{info.tag}</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="inspector-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {info.canEditText && (
          <p className="text-xs text-muted-foreground bg-white/5 border border-white/10 rounded-lg p-2.5">
            Clicca sul testo nell'anteprima e scrivi per modificarlo.
          </p>
        )}

        {info.isImage && (
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Immagine</Label>
            <label className={`w-full h-9 flex items-center justify-center gap-1.5 bg-primary text-black hover:bg-[#33F3FF] text-xs font-semibold rounded-md cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="inspector-upload-label">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Caricamento..." : "Carica dal dispositivo"}
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="inspector-upload-input" />
            </label>
            <div className="text-[11px] text-muted-foreground text-center">oppure incolla un URL</div>
            <Input
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              placeholder="https://..."
              className="bg-white/5 border-white/10 h-9 text-xs"
              data-testid="inspector-src-input"
            />
            <Button onClick={() => onCmd("src", { value: src })} variant="outline" className="w-full h-8 border-white/15 bg-white/5 text-white hover:bg-white/10 text-xs" data-testid="inspector-src-apply">
              Applica URL
            </Button>
          </div>
        )}

        {info.isLink && (
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Link (URL)</Label>
            <Input
              value={href}
              onChange={(e) => setHref(e.target.value)}
              placeholder="https://..."
              className="bg-white/5 border-white/10 h-9 text-xs"
              data-testid="inspector-href-input"
            />
            <Button onClick={() => onCmd("href", { value: href })} className="w-full h-8 bg-primary text-black hover:bg-[#33F3FF] text-xs">
              Applica link
            </Button>
          </div>
        )}

        {info.isText && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Type className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Dimensione testo</span>
              <span className="text-xs ml-auto font-mono">{info.fontSize}px</span>
            </div>
            <Slider
              value={[info.fontSize]}
              min={10}
              max={72}
              step={1}
              onValueChange={(v) => onCmd("style", { prop: "fontSize", value: v[0] + "px" })}
              data-testid="inspector-fontsize"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => onCmd("style", { prop: "fontWeight", value: isBold ? "400" : "700" })}
                className={`h-8 w-8 p-0 border-white/15 ${isBold ? "bg-primary text-black" : "bg-white/5 text-white"}`}
                data-testid="inspector-bold"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <div className="flex-1" />
              {[
                { a: "left", I: AlignLeft },
                { a: "center", I: AlignCenter },
                { a: "right", I: AlignRight },
              ].map(({ a, I }) => (
                <Button
                  key={a}
                  variant="outline"
                  onClick={() => onCmd("style", { prop: "textAlign", value: a })}
                  className={`h-8 w-8 p-0 border-white/15 ${info.textAlign === a ? "bg-primary text-black" : "bg-white/5 text-white"}`}
                  data-testid={`inspector-align-${a}`}
                >
                  <I className="w-4 h-4" />
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 pt-1">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Colori</div>
          {info.isText && (
            <ColorField label="Testo" value={info.color} onChange={(v) => onCmd("style", { prop: "color", value: v })} testid="inspector-text-color" />
          )}
          <ColorField label="Sfondo" value={info.bgColor} onChange={(v) => onCmd("style", { prop: "backgroundColor", value: v })} testid="inspector-bg-color" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Square className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Angoli arrotondati</span>
            <span className="text-xs ml-auto font-mono">{info.radius}px</span>
          </div>
          <Slider
            value={[info.radius]}
            min={0}
            max={48}
            step={2}
            onValueChange={(v) => onCmd("style", { prop: "borderRadius", value: v[0] + "px" })}
            data-testid="inspector-radius"
          />
        </div>
      </div>

      <div className="p-4 border-t border-white/10 shrink-0">
        <Button
          variant="outline"
          onClick={onDelete}
          className="w-full h-9 border-destructive/40 text-destructive hover:bg-destructive/10 text-sm"
          data-testid="inspector-delete"
        >
          <Trash2 className="w-4 h-4 mr-1.5" /> Elimina elemento
        </Button>
      </div>
    </div>
  );
}
